/**
 * /admin/users/:id — single-user detail.
 *
 * Shows profile + last-login + active sessions + audit timeline (last 50
 * events for this user) + admin actions (role change, disable/enable,
 * resend invite, revoke sessions).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  adminUsersApi,
  auditApi,
  type AdminUserDetail,
  type AdminUserSession,
  type AuditEntry,
  type UserRole,
} from '@/api/client';
import { useRolePath } from '@/lib/rolePath';
import {
  Button,
  Card,
  CardContent,
  Input,
  Modal,
  Spinner,
} from '@/components/common';
import {
  ArrowLeft,
  UserCog,
  Shield,
  Monitor,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Trash2,
} from 'lucide-react';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    disabled: 'bg-gray-200 text-gray-600',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}

function HardDeleteModal({
  open,
  user,
  onClose,
  onDeleted,
}: {
  open: boolean;
  user: AdminUserDetail;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset confirmation state every time the modal opens/closes.
  useEffect(() => {
    if (!open) {
      setTyped('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (typed.trim().toLowerCase() !== user.email.toLowerCase()) {
      setError('email must match exactly');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await adminUsersApi.hardDelete(user.id);
      onDeleted();
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail || 'delete failed';
      setError(typeof detail === 'string' ? detail : 'delete failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Delete user permanently">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p>
              This action is <strong>permanent</strong>. It cannot be undone.
            </p>
            <ul className="list-disc list-inside text-xs space-y-0.5">
              <li>Their Cognito account is deleted (the email can be re-invited later).</li>
              <li>Their DB row is removed; all of their sessions, invitations, and password-reset tokens are wiped.</li>
              <li>Folders + items they created stay; their ownership becomes <code>null</code>.</li>
              <li>Any users they themselves invited will have <code>invited by</code> cleared.</li>
            </ul>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type <code className="bg-gray-100 px-1 rounded">{user.email}</code> to confirm
          </label>
          <Input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={user.email}
            autoComplete="off"
            disabled={submitting}
            fullWidth
          />
        </div>

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={submitting}
            disabled={
              submitting ||
              typed.trim().toLowerCase() !== user.email.toLowerCase()
            }
            className="bg-red-600 hover:bg-red-700"
          >
            Delete permanently
          </Button>
        </div>
      </form>
    </Modal>
  );
}


export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const rp = useRolePath();
  const userId = id ? parseInt(id, 10) : NaN;

  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [sessions, setSessions] = useState<AdminUserSession[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [actionInFlight, setActionInFlight] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, ss, ap] = await Promise.all([
        adminUsersApi.get(userId),
        adminUsersApi.getSessions(userId),
        auditApi.list({ user_id: userId, per_page: 50 }),
      ]);
      setUser(u);
      setSessions(ss);
      setAudit(ap.items);
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail || 'could not load user';
      setError(typeof detail === 'string' ? detail : 'could not load user');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isNaN(userId)) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setActionInFlight(true);
    setFlash(null);
    try {
      await fn();
      setFlash(label);
      await loadAll();
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail || `${label} failed`;
      alert(`${label} failed: ${typeof detail === 'string' ? detail : 'unknown'}`);
    } finally {
      setActionInFlight(false);
    }
  };

  if (isNaN(userId)) {
    return <p className="text-red-600">Invalid user id</p>;
  }

  if (loading) {
    return <div className="py-12 text-center"><Spinner size="xl" /></div>;
  }

  if (error || !user) {
    return (
      <Card>
        <CardContent>
          <div className="flex items-start gap-3 text-red-600">
            <AlertTriangle size={20} />
            <div>
              <p className="font-medium">Could not load user</p>
              <p className="text-sm">{error}</p>
              <Button variant="ghost" size="sm" onClick={() => navigate(rp('/users'))} className="mt-3">
                Back to Users
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Back + flash */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" leftIcon={<ArrowLeft size={16} />} onClick={() => navigate(rp('/users'))}>
          Back to Users
        </Button>
        <Button variant="ghost" size="sm" leftIcon={<RefreshCw size={14} />} onClick={loadAll}>
          Refresh
        </Button>
      </div>

      {flash && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-700">
          <CheckCircle2 size={20} />
          <span className="text-sm">{flash}</span>
          <button className="ml-auto text-xs underline" onClick={() => setFlash(null)}>dismiss</button>
        </div>
      )}

      {/* Profile card */}
      <Card>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center">
              <UserCog size={28} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-gray-900 truncate">
                {user.username || user.email}
              </h2>
              <p className="text-sm text-gray-500 truncate">{user.email}</p>
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                <span className="capitalize">{user.role}</span>
                <span>·</span>
                <StatusBadge status={user.status} />
                {user.role === 'admin' && <Shield size={14} className="text-primary" />}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-6">
            <div>
              <p className="text-gray-500">Username</p>
              <p className="font-medium text-gray-900">{user.username || '—'}</p>
            </div>
            <div>
              <p className="text-gray-500">Company</p>
              <p className="font-medium text-gray-900">{user.company || '—'}</p>
            </div>
            <div>
              <p className="text-gray-500">Last sign-in</p>
              <p className="font-medium text-gray-900">{formatDate(user.last_login_at)}</p>
            </div>
            <div>
              <p className="text-gray-500">Last IP</p>
              <p className="font-medium text-gray-900">{user.last_login_ip || '—'}</p>
            </div>
            <div>
              <p className="text-gray-500">Failed login count</p>
              <p className="font-medium text-gray-900">{user.failed_login_count}</p>
            </div>
            <div>
              <p className="text-gray-500">Locked until</p>
              <p className="font-medium text-gray-900">{formatDate(user.locked_until)}</p>
            </div>
            <div>
              <p className="text-gray-500">Created at</p>
              <p className="font-medium text-gray-900">{formatDate(user.created_at)}</p>
            </div>
            <div>
              <p className="text-gray-500">Invited by</p>
              <p className="font-medium text-gray-900">
                {user.invited_by_username || user.invited_by || '—'}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
            {user.status === 'pending' && (
              <Button
                variant="ghost"
                size="sm"
                disabled={actionInFlight}
                onClick={() => run(`Invitation resent to ${user.email}`,
                  () => adminUsersApi.resendInvite(user.id))}
              >
                Resend invitation
              </Button>
            )}

            {user.status === 'disabled' ? (
              <Button
                variant="primary"
                size="sm"
                disabled={actionInFlight}
                onClick={() => run(`Re-enabled ${user.email}`,
                  () => adminUsersApi.enable(user.id))}
              >
                Re-enable user
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                disabled={actionInFlight}
                onClick={() => {
                  if (!window.confirm(`Disable ${user.email}? They lose access immediately.`)) return;
                  run(`Disabled ${user.email}`, () => adminUsersApi.disable(user.id));
                }}
              >
                Disable user
              </Button>
            )}

            {user.role === 'admin' ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={actionInFlight}
                onClick={() => {
                  if (!window.confirm(`Demote ${user.email} from admin to user? Their sessions will be revoked.`)) return;
                  run(`Demoted ${user.email} to user`,
                    () => adminUsersApi.changeRole(user.id, 'user' as UserRole));
                }}
              >
                Demote to user
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                disabled={actionInFlight}
                onClick={() => {
                  if (!window.confirm(`Promote ${user.email} to admin? They will be able to manage all users, nodes, and deployments. Their sessions will be revoked.`)) return;
                  run(`Promoted ${user.email} to admin`,
                    () => adminUsersApi.changeRole(user.id, 'admin' as UserRole));
                }}
              >
                Promote to admin
              </Button>
            )}

            {sessions.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                disabled={actionInFlight}
                onClick={() => {
                  if (!window.confirm(`Revoke all ${sessions.length} active session(s) for ${user.email}?`)) return;
                  run(`Revoked ${sessions.length} session(s) for ${user.email}`,
                    () => adminUsersApi.revokeSessions(user.id));
                }}
              >
                Revoke all sessions
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Trash2 size={14} />}
              disabled={actionInFlight}
              onClick={() => setDeleteOpen(true)}
              className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Delete permanently
            </Button>
          </div>
        </CardContent>
      </Card>

      <HardDeleteModal
        open={deleteOpen}
        user={user}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false);
          navigate(rp('/users'), {
            state: { flash: `Permanently deleted ${user.email}` },
          });
        }}
      />

      {/* Active sessions */}
      <Card>
        <CardContent>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Active sessions ({sessions.length})</h3>
          {sessions.length === 0 ? (
            <p className="text-gray-500 text-sm">No active sessions.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-gray-500 border-b border-gray-200">
                  <tr>
                    <th className="py-2 pr-3">Device</th>
                    <th className="py-2 pr-3">IP</th>
                    <th className="py-2 pr-3">Created</th>
                    <th className="py-2 pr-3">Last used</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id} className="border-b border-gray-100">
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <Monitor size={16} className="text-gray-400" />
                          <span title={s.user_agent ?? ''}>{(s.user_agent || 'unknown').slice(0, 80)}</span>
                          {s.remember_me && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">remembered</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-gray-600">{s.ip || '—'}</td>
                      <td className="py-2 pr-3 text-gray-600">{formatDate(s.created_at)}</td>
                      <td className="py-2 pr-3 text-gray-600">{formatDate(s.last_used_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit timeline */}
      <Card>
        <CardContent>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Audit timeline (last 50)</h3>
          {audit.length === 0 ? (
            <p className="text-gray-500 text-sm">No audit events for this user yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {audit.map((a) => (
                <li key={a.id} className="flex items-start gap-3 p-2 rounded hover:bg-gray-50">
                  <span className="text-xs text-gray-400 w-44 flex-shrink-0">{formatDate(a.timestamp)}</span>
                  <span
                    className={
                      'text-xs px-2 py-0.5 rounded font-mono ' +
                      (a.status === 'success'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-red-50 text-red-700')
                    }
                  >
                    {a.operation}
                  </span>
                  <span className="text-gray-700 text-xs">
                    by <strong>{a.initiated_by || '—'}</strong>
                    {a.details && typeof a.details === 'object' && 'ip' in a.details && (
                      <span className="text-gray-500"> from {String(a.details.ip)}</span>
                    )}
                    {a.error_message && <span className="text-red-600"> · {a.error_message}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
